import { useCallback, useMemo } from "react";
import {
  useFilterState,
  DEFAULT_DATE_PRESETS,
  type UseFilterStateResult,
  type FilterDef,
} from "@salestrak/ui";

// Re-export library types so existing consumer imports still work
export type {
  FilterDef,
  SelectFilterDef,
  EntityFilterDef,
  DateFilterDef,
  TableFilter,
} from "@salestrak/ui";

import { combineWhereConditions } from "#app/lib/query-utils";

// --- Extended result with Prisma where-clause support ---

// `TItem` threads through to the library's `TableFilter<TItem>[]`, so a table
// that filters Members gets Member-typed filters instead of `unknown`.
export type UseTableFiltersResult<TItem = unknown> =
  UseFilterStateResult<TItem> & {
    where: Record<string, unknown> | undefined;
    getWhereExcluding: (
      excludeKey: string,
    ) => Record<string, unknown> | undefined;
  };

// --- Prisma-specific where builder (stays in consumer) ---

function buildWhere<TItem>(def: FilterDef<TItem>, values: string[]): Record<string, unknown> | undefined {
  if (values.length === 0) return undefined;

  if (def.type === 'select') {
    const op = def.operator ?? "hasSome";
    const multiple = def.multiple ?? true;
    if (!multiple) return { [def.key]: { [op]: values[0] } };
    return { [def.key]: { [op]: values } };
  }

  if (def.type === "entity") {
    const op = def.operator ?? "hasSome";
    const format = def.filterFormat ?? "idArray";
    if (format === "entityRef") {
      return { [def.key]: { entityId: { [op]: values } } };
    }
    return { [def.key]: { [op]: values } };
  }

  if (def.type === "date") {
    const presets = def.presets ?? DEFAULT_DATE_PRESETS;
    const preset = presets.find((p) => p.value === values[0]);
    if (!preset) return undefined;
    const date = new Date();
    date.setDate(date.getDate() - preset.days);
    date.setHours(0, 0, 0, 0);
    return { [def.key]: { gte: date.toISOString() } };
  }

  return undefined;
}

// --- Hook ---

export function useTableFilters<TItem = unknown>(
  filterDefs: FilterDef<TItem>[],
): UseTableFiltersResult<TItem> {
  const filterState = useFilterState<TItem>(filterDefs);

  const where = useMemo(() => {
    const conditions: Record<string, unknown>[] = [];
    for (const def of filterDefs) {
      const values = filterState.filterValues[def.key] ?? [];
      const w = buildWhere(def, values);
      if (w) conditions.push(w);
    }
    return combineWhereConditions(conditions);
  }, [filterDefs, filterState.filterValues]);

  const getWhereExcluding = useCallback(
    (excludeKey: string) => {
      const conditions: Record<string, unknown>[] = [];
      for (const def of filterDefs) {
        if (def.key === excludeKey) continue;
        const values = filterState.filterValues[def.key] ?? [];
        const w = buildWhere(def, values);
        if (w) conditions.push(w);
      }
      return combineWhereConditions(conditions);
    },
    [filterDefs, filterState.filterValues],
  );

  return useMemo(() => ({
    ...filterState,
    where,
    getWhereExcluding,
  }), [filterState, where, getWhereExcluding]);
}
